/**
 * LH Identity — lh-identity.js   (For You · Phase 2a)
 *
 * Resolves a browsing visitor's contact identity (email / phone) from signals
 * we legitimately own, into a first-party cookie `lh_identity`, so the "For You"
 * browse sync can fire for ANY known subscriber — not only logged-in Shopify
 * customers.
 *
 * Sources (whichever fire, best-wins):
 *   1. Shopify logged-in customer  → window.__lhCustomer = { email, phone }
 *   2. Email/phone the visitor TYPES into an on-site form (newsletter / signup /
 *      account / Klaviyo inline forms). Captured on `change` (fires on blur), so
 *      it works even when the form submits via JS/AJAX.
 *
 * PRIVACY: only stores contact info the visitor gives us directly on our own
 * pages. It never reads Attentive/Klaviyo private data. The value is sent to our
 * endpoint in POST bodies only — never in a URL. Downstream, only opted-in
 * subscribers are ever messaged (Attentive/Klaviyo enforce that), so a captured
 * address that isn't a subscriber is a harmless no-op.
 *
 * NOTE (Phase 2b, not here): reading platform cookies (__kla_id / _kx),
 * marketing-link identifiers, and writing the token to Klaviyo are deferred and
 * need per-platform API confirmation — see docs/for-you-phase2-identity.md.
 *
 * Public API: window.LHIdentity = { get(), set(email, phone, source) }
 */
(function (global) {
  'use strict';

  var COOKIE     = 'lh_identity';
  var COOKIE_DAYS = 365;
  var EMAIL_RE   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var E164_RE    = /^\+[1-9]\d{9,14}$/;

  // ── cookie helpers ─────────────────────────────────────────────────────────
  function readCookie(name) {
    try {
      var m = document.cookie.match(
        new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)')
      );
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }
  function writeCookie(name, value, days) {
    try {
      var exp = '';
      if (days) {
        var d = new Date();
        d.setTime(d.getTime() + days * 864e5);
        exp = '; expires=' + d.toUTCString();
      }
      document.cookie = name + '=' + encodeURIComponent(value) + exp + '; path=/; SameSite=Lax';
    } catch (e) {}
  }

  // ── store ──────────────────────────────────────────────────────────────────
  function get() {
    try {
      var raw = readCookie(COOKIE);
      if (!raw) return null;
      var obj = JSON.parse(raw);
      return (obj && (obj.email || obj.phone)) ? obj : null;
    } catch (e) { return null; }
  }

  function cleanEmail(v) {
    v = (v || '').trim().toLowerCase();
    return EMAIL_RE.test(v) ? v : '';
  }
  // Only accept an unambiguous E.164 phone (leading +, 10–15 digits) so we never
  // push malformed numbers to Attentive.
  function cleanPhone(v) {
    v = (v || '').trim();
    return E164_RE.test(v) ? v : '';
  }

  /**
   * set — merge a new email/phone into the stored identity. Never downgrades an
   * existing value to blank. Returns the stored identity (or null if nothing usable).
   */
  function set(email, phone, source) {
    var e = cleanEmail(email);
    var p = cleanPhone(phone);
    if (!e && !p) return get();

    var cur = get() || {};
    var next = {
      email : e || cur.email || '',
      phone : p || cur.phone || '',
      source: source || cur.source || '',
      ts    : Math.floor(Date.now() / 1000)
    };
    if (cur.email === next.email && cur.phone === next.phone) return next; // unchanged
    writeCookie(COOKIE, JSON.stringify(next), COOKIE_DAYS);
    // Let listeners (e.g. the For You sync) react the moment identity is learned.
    try { document.dispatchEvent(new CustomEvent('lh:identity', { detail: next })); } catch (e) {}
    return next;
  }

  // ── auto-capture ─────────────────────────────────────────────────────────--
  function seedFromShopify() {
    try {
      var c = global.__lhCustomer;
      if (c && (c.email || c.phone)) set(c.email, c.phone, 'shopify-account');
    } catch (e) {}
  }

  function onFieldChange(e) {
    try {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      var type = (t.type || '').toLowerCase();
      var name = (t.name || '').toLowerCase();
      var looksEmail = type === 'email' || name.indexOf('email') !== -1;
      if (!looksEmail) return;

      var email = cleanEmail(t.value);
      if (!email) return;

      // Best-effort matching phone from the same form
      var phone = '';
      var form = t.form || (t.closest ? t.closest('form') : null);
      if (form) {
        var tel = form.querySelector('input[type="tel"], input[name*="phone" i]');
        if (tel) phone = tel.value;
      }
      set(email, phone, 'onsite-form');
    } catch (e2) {}
  }

  function attach() {
    seedFromShopify();
    // Capture phase so we still see the value even if the handler stops propagation.
    document.addEventListener('change', onFieldChange, true);
  }

  global.LHIdentity = { get: get, set: set };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attach);
  } else {
    attach();
  }
})(window);
